import * as XLSX from "xlsx-js-style";
import { SERVER_URL } from "../api";

/** Columnas solicitadas (solo inscripciones con pago completado). */
const HEADERS = [
  "DNI",
  "Correo",
  "Nombre",
  "Apellido 1",
  "Apellido 2",
  "Plan",
  "Precio",
  "Teléfono",
  "Fecha de nacimiento",
  "Población",
  "Género",
  "Código postal",
  "Titulación",
  "Situación laboral",
  "Centro de trabajo",
  "Matrícula",
];

const LAST_COL = HEADERS.length - 1;

/** Anchos de columna (caracteres aprox.) para correo, textos largos y cabeceras completas. */
const COL_WIDTHS = [
  { wch: 12 }, // DNI
  { wch: 40 }, // Correo
  { wch: 14 }, // Nombre
  { wch: 16 }, // Apellido 1
  { wch: 16 }, // Apellido 2
  { wch: 18 }, // Plan
  { wch: 12 }, // Precio
  { wch: 14 }, // Teléfono
  { wch: 14 }, // Fecha de nacimiento
  { wch: 22 }, // Población
  { wch: 12 }, // Género
  { wch: 11 }, // Código postal
  { wch: 22 }, // Titulación
  { wch: 24 }, // Situación laboral
  { wch: 30 }, // Centro de trabajo
  { wch: 26 }, // Matrícula (texto corto + enlace)
];

const BORDER_GRID = {
  top: { style: "thin", color: { rgb: "BFBFBF" } },
  bottom: { style: "thin", color: { rgb: "BFBFBF" } },
  left: { style: "thin", color: { rgb: "BFBFBF" } },
  right: { style: "thin", color: { rgb: "BFBFBF" } },
};

function str(v) {
  if (v == null || v === undefined) return "";
  if (typeof v === "boolean") return v ? "Sí" : "No";
  return String(v).trim();
}

function formatEur(n) {
  const num = Number(n);
  if (Number.isNaN(num)) return "";
  try {
    return new Intl.NumberFormat("es-ES", {
      style: "currency",
      currency: "EUR",
      minimumFractionDigits: Number.isInteger(num) ? 0 : 2,
      maximumFractionDigits: 2,
    }).format(num);
  } catch {
    return String(num);
  }
}

function mediaUrl(path) {
  if (!path) return "";
  const p = String(path).trim().replace(/^\//, "");
  if (!p) return "";
  if (/^https?:\/\//i.test(p)) return p;
  return `${SERVER_URL}/${p}`;
}

/** Igual que en VerCongresoEdicionPage: ruta relativa, uploads/… o URL absoluta. */
function inscripcionAdjuntoPublicUrl(raw) {
  if (raw == null || raw === "") return "";
  const s = String(raw).trim().replace(/\\/g, "/");
  if (!s) return "";
  if (/^https?:\/\//i.test(s)) return s;
  const lower = s.toLowerCase();
  const idx = lower.indexOf("uploads/");
  const rel = idx >= 0 ? s.slice(idx) : s.replace(/^\//, "");
  return mediaUrl(rel);
}

/**
 * Celda de matrícula: si hay adjunto, hipervínculo a la imagen/documento (no el nombre del fichero).
 * @returns {string|import('xlsx').CellObject}
 */
function matriculaCell(fd) {
  const m = str(fd?.matricula);
  const raw = str(fd?.archivoTemporal);
  const adjuntoUrl = raw ? inscripcionAdjuntoPublicUrl(raw) : "";

  if (adjuntoUrl) {
    const label = m ? `${m} (ver documento)` : "Ver documento adjunto";
    return {
      t: "s",
      v: label,
      l: { Target: adjuntoUrl, Tooltip: "Abrir matrícula o documento adjunto" },
    };
  }

  if (m && /^https?:\/\//i.test(m)) {
    return {
      t: "s",
      v: "Abrir enlace",
      l: { Target: m },
    };
  }

  return m;
}

function rowFromInscripcion(r) {
  const fd = r.formData && typeof r.formData === "object" ? r.formData : {};
  const correo = str(r.emailContacto) || str(fd.email);
  const precio =
    formatEur(r.precioImporte) || str(r.precioTextoSnapshot) || "";
  return [
    str(fd.dni),
    correo,
    str(fd.nombre),
    str(fd.apellido1),
    str(fd.apellido2),
    str(r.planTituloSnapshot) || str(r.planId),
    precio,
    str(fd.telefono),
    str(fd.fechaNacimiento),
    str(fd.poblacion),
    str(fd.genero),
    str(fd.codigoPostal),
    str(fd.titulacion),
    str(fd.situacionLaboral),
    str(fd.centroTrabajo),
    matriculaCell(fd),
  ];
}

/** Columnas con texto largo: alineación superior + ajuste de línea. */
function dataColumnWrapText(col) {
  return [1, 8, 9, 12, 13, 14].includes(col);
}

function applySheetVisuals(ws, sections) {
  ws["!cols"] = COL_WIDTHS;

  const merges = [];
  const rowHeights = [];

  const planStyle = {
    font: { bold: true, sz: 12, color: { rgb: "FFFFFF" } },
    fill: { patternType: "solid", fgColor: { rgb: "E97132" } },
    alignment: { horizontal: "left", vertical: "center", wrapText: false },
    border: BORDER_GRID,
  };

  const headerStyle = {
    font: { bold: true, sz: 11, color: { rgb: "1F1F1F" } },
    fill: { patternType: "solid", fgColor: { rgb: "D6DCE5" } },
    alignment: { horizontal: "center", vertical: "center", wrapText: true },
    border: BORDER_GRID,
  };

  for (const sec of sections) {
    merges.push({
      s: { r: sec.planRow, c: 0 },
      e: { r: sec.planRow, c: LAST_COL },
    });
    rowHeights[sec.planRow] = { hpt: 28 };

    const planRef = XLSX.utils.encode_cell({ r: sec.planRow, c: 0 });
    const planCell = ws[planRef];
    if (planCell) {
      planCell.s = { ...planStyle };
    }

    for (let c = 0; c <= LAST_COL; c++) {
      const ref = XLSX.utils.encode_cell({ r: sec.headerRow, c });
      const cell = ws[ref];
      if (!cell) continue;
      cell.s = { ...headerStyle };
    }

    let zebra = false;
    if (sec.dataEnd >= sec.dataStart) {
      for (let r = sec.dataStart; r <= sec.dataEnd; r++) {
        rowHeights[r] = { hpt: 22 };
        for (let c = 0; c <= LAST_COL; c++) {
          const ref = XLSX.utils.encode_cell({ r, c });
          const cell = ws[ref];
          if (!cell) continue;
          const wrap = dataColumnWrapText(c);
          const zebraFill = zebra
            ? { patternType: "solid", fgColor: { rgb: "F5F6F8" } }
            : { patternType: "solid", fgColor: { rgb: "FFFFFF" } };

          const align = {
            vertical: "top",
            wrapText: c === 15 ? false : wrap,
            horizontal:
              c === 6 || c === 11
                ? "center"
                : c === 15
                  ? "center"
                  : "left",
          };

          cell.s = {
            font: { sz: 11, color: { rgb: "222222" } },
            fill: zebraFill,
            alignment: align,
            border: BORDER_GRID,
          };
        }
        zebra = !zebra;
      }
    }
  }

  if (merges.length) ws["!merges"] = merges;
  ws["!rows"] = rowHeights;
}

/**
 * Construye la hoja de inscripciones agrupadas por plan (planes más caros primero).
 * @param {Array<{ label: string, maxPrice: number, rows: object[], planId?: string }>} groups
 * @returns {import('xlsx').WorkSheet|null}
 */
export function buildInscripcionesWorksheet(groups) {
  if (!Array.isArray(groups) || groups.length === 0) return null;

  const aoa = [];
  /** @type {{ planRow: number, headerRow: number, dataStart: number, dataEnd: number }[]} */
  const sections = [];
  let rowIndex = 0;

  for (const g of groups) {
    const n = g.rows && g.rows.length ? g.rows.length : 0;
    const planLine = `${g.label || g.planId || "Plan"} — ${formatEur(g.maxPrice) || "—"} (${n} inscrito(s))`;

    const planRow = rowIndex;
    aoa.push([planLine]);
    rowIndex += 1;

    const headerRow = rowIndex;
    aoa.push(HEADERS);
    rowIndex += 1;

    const dataStart = rowIndex;
    for (const row of g.rows || []) {
      aoa.push(rowFromInscripcion(row));
      rowIndex += 1;
    }
    const dataEnd = rowIndex - 1;

    sections.push({
      planRow,
      headerRow,
      dataStart,
      dataEnd: dataEnd >= dataStart ? dataEnd : dataStart - 1,
    });

    aoa.push([]);
    rowIndex += 1;
  }

  const ws = XLSX.utils.aoa_to_sheet(aoa);
  applySheetVisuals(ws, sections);
  return ws;
}

function excelDateStamp() {
  const d = new Date();
  const pad = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}${pad(d.getMonth() + 1)}${pad(d.getDate())}`;
}

/**
 * Exporta a .xlsx las inscripciones finalizadas, agrupadas por plan (planes más caros primero).
 * @param {{ year?: number|string, finalizedGroups: Array<{ label: string, maxPrice: number, rows: object[] }> }} params
 */
export function exportCongresoInscripcionesExcel({ year, finalizedGroups }) {
  const ws = buildInscripcionesWorksheet(finalizedGroups);
  if (!ws) return false;

  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, "Inscritos");

  const y = year != null && year !== "" ? String(year) : "edicion";
  XLSX.writeFile(wb, `inscripciones-congreso-${y}-${excelDateStamp()}.xlsx`);
  return true;
}
